/**
 * A provider's fleet or equipment list → register rows (provider Assets).
 * Spreadsheets (by their headings) and pasted lines are read by code, on this
 * computer; photos, PDFs and what the code can't read go to the AI reader
 * (/ai/fleet/read). Vehicles and equipment only — never people.
 */

export type FleetKind = "vehicle" | "equipment" | "other";

export interface FleetItem {
    kind: FleetKind;
    make: string | null;
    model: string | null;
    year: number | null;
    plate: string | null;
    armoured: boolean | null;
    colour: string | null;
    insuranceExpires: string | null;
}

const HEADERS: Record<string, RegExp> = {
    plate: /\b(plate|registration|reg\.?\s*(no|number)?|licen[cs]e\s*(plate|no|number)|number\s*plate|serial|s\/n)\b|رقم اللوحة|اللوحة/i,
    make: /\b(make|manufacturer|brand)\b|الشركة المصنعة|الماركة/i,
    model: /\b(model|vehicle|description|item|equipment)\b|الطراز|الموديل/i,
    year: /\b(year|model\s*year|yr)\b|سنة/i,
    kind: /\b(type|category|kind)\b|النوع/i,
    armour: /\b(armou?r(ed)?|ballistic|protection\s*level|b[4-7])\b|مصفح/i,
    colour: /\b(colou?r)\b|اللون/i,
    insurance: /\b(insurance|insured\s*(until|to)|policy\s*expir\w*)\b|التأمين/i,
};

const EQUIPMENT = /\b(radios?|vests?|body\s*armou?r|helmets?|sat(ellite)?\s*phones?|gps|trackers?|jammers?|drones?|cameras?|kits?|equipment)\b/i;
const YES = /^(y|yes|true|1|armou?red|b[4-7]|✓|x)$/i;

const isoDate = (value: string): string | null => {
    const text = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
    const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(text);
    return dmy ? `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}` : null;
};

/**
 * Rows from a spreadsheet: the heading row (in the first 10) that names at
 * least a plate or a model column; null when there's none (then the AI reads it).
 */
export const fleetFromRows = (rows: string[][]): FleetItem[] | null => {
    for (let at = 0; at < Math.min(rows.length, 10); at += 1) {
        const headers = rows[at].map((cell) => String(cell ?? "").trim());
        const column = (key: string) => headers.findIndex((header) => header && HEADERS[key].test(header));
        const plateAt = column("plate");
        const modelAt = column("model");
        const makeAt = column("make");
        if (plateAt < 0 && modelAt < 0 && makeAt < 0) continue;
        const yearAt = column("year");
        const kindAt = column("kind");
        const armourAt = column("armour");
        const colourAt = column("colour");
        const insuranceAt = column("insurance");
        return rows.slice(at + 1).flatMap((row) => {
            const cell = (index: number) => (index >= 0 ? String(row[index] ?? "").trim() : "");
            const make = cell(makeAt) || null;
            const model = cell(modelAt) || null;
            const plate = cell(plateAt) ? cell(plateAt).toUpperCase() : null;
            if (!make && !model && !plate) return [];
            const year = Number(cell(yearAt));
            const describe = `${cell(kindAt)} ${model ?? ""}`;
            const armourCell = cell(armourAt);
            return [{
                kind: EQUIPMENT.test(describe) ? "equipment" : "vehicle",
                make, model,
                year: Number.isInteger(year) && year > 1950 && year < 2100 ? year : null,
                plate,
                armoured: armourAt >= 0 ? (armourCell ? YES.test(armourCell) || /\bb[4-7]\b/i.test(armourCell) : false) : /armou?r/i.test(describe) ? true : null,
                colour: cell(colourAt) || null,
                insuranceExpires: isoDate(cell(insuranceAt)),
            } as FleetItem];
        });
    }
    return null;
};

const MAKES = ["Mercedes-Benz", "Mercedes", "Land Rover", "Range Rover", "Toyota", "Lexus", "Nissan", "Chevrolet", "GMC", "Ford", "BMW", "Audi", "Cadillac",
    "Hyundai", "Kia", "Mitsubishi", "Honda", "Volkswagen", "VW", "Peugeot", "Renault", "Jeep", "Dodge", "Lincoln", "Infiniti", "Mazda", "Suzuki", "Isuzu",
    "Motorola", "Garmin", "Iridium", "Thuraya", "Kenwood", "Hytera", "Inmarsat"];
const COLOURS = /\b(white|black|silver|grey|gray|blue|red|beige|gold|green|brown|maroon|champagne|bronze)\b/i;
const NOT_ARMOURED = /\bnot\s+armou?red\b|\bnon[-\s]?armou?red\b|\bunarmou?red\b|\bsoft[\s-]?skin\b/i;
const ARMOURED = /\barmou?red\b|\bb[4-7]\b|\bbr[4-7]\b/i;
const DATE = String.raw`(\d{1,2}[/.-]\d{1,2}[/.-]\d{4}|\d{4}-\d{2}-\d{2})`;

/** "871TKJ2201–871TKJ2206" → the serials in between, when the range is plain (same prefix, at most 50). */
const serialRange = (text: string): string[] | null => {
    const range = /\b([A-Z0-9]*?[A-Z])?(\d{2,})\s*[–-]\s*([A-Z0-9]*?[A-Z])?(\d{2,})\b/i.exec(text);
    if (!range || (range[1] ?? "") !== (range[3] ?? "") || range[2].length !== range[4].length) return null;
    const from = Number(range[2]);
    const to = Number(range[4]);
    if (!(to >= from) || to - from >= 50) return null;
    return Array.from({ length: to - from + 1 }, (_, index) => `${range[1] ?? ""}${String(from + index).padStart(range[2].length, "0")}`);
};

/** One vehicle line: "Toyota Land Cruiser 300, 2023, B6 armoured, white, plate Dubai P 48213, insurance until 01/04/2027". */
const vehicleFrom = (line: string): FleetItem | null => {
    const make = MAKES.find((name) => new RegExp(`(?<![\\p{L}])${name.replace(/[-]/g, "[-\\s]?")}(?![\\p{L}])`, "iu").test(line)) ?? null;
    const plate = /\bplate\s*(?:no\.?|number)?\s*[:#]?\s*([^,;()]+)/i.exec(line)?.[1]?.trim() ?? null;
    if (!make && !plate) return null;
    const afterMake = make ? line.slice(line.search(new RegExp(make.replace(/[-]/g, "[-\\s]?"), "i")) + make.length) : "";
    const model = make ? afterMake.split(/[,;(]/)[0].replace(/^[\s-]+/, "").trim() || null : line.split(/[,;(]/)[0].trim() || null;
    const yearHit = /(?<![\d/.-])(19[5-9]\d|20\d{2})(?![\d/.-])/.exec(line.replace(/\bplate\b.*?(,|$)/i, ""));
    const insurance = new RegExp(`\\binsur\\w*\\s*(?:until|till|to|expires?|exp\\.?|valid\\s+(?:until|to))?\\s*:?\\s*${DATE}`, "i").exec(line)?.[1] ?? null;
    return {
        kind: EQUIPMENT.test(line) ? "equipment" : "vehicle",
        make: make === "VW" ? "Volkswagen" : make,
        model,
        year: yearHit ? Number(yearHit[1]) : null,
        plate,
        armoured: NOT_ARMOURED.test(line) ? false : ARMOURED.test(line) ? true : null,
        colour: COLOURS.exec(line)?.[1]?.toLowerCase() ?? null,
        insuranceExpires: insurance ? isoDate(insurance) : null,
    };
};

/** "Equipment: 6 x Motorola DP4800e radios (serials 871TKJ2201–871TKJ2206), 2 x Garmin inReach Mini 2 satellite trackers". */
const equipmentFrom = (line: string): FleetItem[] => {
    const body = line.replace(/^\s*(equipment|kit|other)\s*:\s*/i, "");
    // split on commas outside brackets
    const parts: string[] = [];
    let depth = 0;
    let current = "";
    for (const char of body) {
        if (char === "(") depth += 1;
        if (char === ")") depth = Math.max(0, depth - 1);
        if (char === "," && depth === 0) { parts.push(current); current = ""; } else current += char;
    }
    parts.push(current);
    return parts.flatMap((part) => {
        const item = /^\s*(\d{1,2})\s*[x×]\s*(.+)$/i.exec(part);
        const count = item ? Math.min(Number(item[1]), 20) : 1;
        const described = (item ? item[2] : part).trim();
        if (!described || !EQUIPMENT.test(described)) return [];
        const name = described.replace(/\s*\([^)]*\)\s*/g, " ").trim();
        const make = MAKES.find((brand) => new RegExp(`^${brand}\\b`, "i").test(name)) ?? null;
        const serials = serialRange(described);
        return Array.from({ length: count }, (_, index) => ({
            kind: "equipment" as const,
            make,
            model: make ? name.slice(make.length).trim() || null : name,
            year: null,
            plate: serials && serials.length === count ? serials[index] : null,
            armoured: null, colour: null, insuranceExpires: null,
        }));
    });
};

/**
 * A pasted fleet list read by code, on this computer (no AI, no credit): one
 * vehicle per line that names a make or a plate, and equipment as "N x item"
 * (a plain serial range gives each item its serial). People — a driver on
 * call — are never items. Null when nothing reads as a vehicle or equipment.
 * @param text - The pasted list.
 */
export const fleetFromText = (text: string): FleetItem[] | null => {
    const items = String(text ?? "").split(/\r?\n/).flatMap((raw) => {
        const line = raw.replace(/^\s*(?:\d{1,3}[.)]|[-•*])\s*/, "").trim();
        if (!line || /\b(driver|chauffeur|contact|call)\b[^,]*\+?\d[\d\s-]{6,}/i.test(line)) return [];
        if (/^\s*(equipment|kit)\s*:/i.test(line) || /^\s*\d{1,2}\s*[x×]\s/i.test(line)) return equipmentFrom(line);
        const vehicle = vehicleFrom(line);
        return vehicle ? [vehicle] : [];
    });
    return items.length ? items : null;
};

/** One item as a new register row (unsaved). The make goes in front of the model; armoured shows in the name. */
export const fleetRecord = (item: FleetItem): Record<string, unknown> => ({
    model: [item.make, item.model, item.year, item.armoured ? "(armoured)" : null, item.colour ? `· ${item.colour}` : null].filter(Boolean).join(" "),
    kind: item.kind,
    plate: item.plate ?? "",
    insuranceExpires: item.insuranceExpires,
});

/** A short line for the review list. */
export const fleetSummary = (item: FleetItem): string =>
    [[item.make, item.model, item.year].filter(Boolean).join(" ") || "Item", item.plate, item.armoured ? "armoured" : null, item.colour,
        item.insuranceExpires ? `insured to ${item.insuranceExpires}` : null].filter(Boolean).join(" · ");
